/**
 * 📊 V208.0: PostHog Property Builders
 * 
 * Shared property extraction functions to ensure consistency
 * and prevent PII (Personally Identifiable Information) from being tracked.
 */

import { IS_WEB, IS_ANDROID } from '@/config/channel';

export interface BaseProperties {
  platform: 'web' | 'android' | 'ios';
  timestamp?: string;
  user_id?: string;
  session_id?: string;
}

/**
 * Build base properties for all events
 */
export function buildBaseProperties(userId?: string): BaseProperties {
  return {
    platform: IS_WEB ? 'web' : IS_ANDROID ? 'android' : 'ios',
    timestamp: new Date().toISOString(),
    ...(userId && { user_id: userId }),
    ...(typeof window !== 'undefined' && { session_id: getSessionId() }),
  };
}

/**
 * Get or create session ID
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  
  const key = 'posthog_session_id';
  let sessionId = sessionStorage.getItem(key);
  
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(key, sessionId);
  }
  
  return sessionId;
}

/**
 * Build properties for user identification
 * NO PII - only user_id and non-sensitive traits
 */
export function buildUserProperties(userId: string, traits?: Record<string, any>): Record<string, any> {
  const safeTraits: Record<string, any> = {
    ...buildBaseProperties(userId),
  };
  
  // Only include safe traits (no email, name, phone, etc.)
  if (traits) {
    // Allowed non-PII traits
    const allowedKeys = [
      'plan',
      'subscription_status',
      'created_at',
      'profile_completed',
      'verification_status',
      'connection_count',
      'message_count',
    ];
    
    for (const key of allowedKeys) {
      if (key in traits) {
        safeTraits[key] = traits[key];
      }
    }
  }
  
  return safeTraits;
}

// WARNING: Never include query params or hash fragments — may contain PII.
function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`; // strips query + hash
  } catch {
    return 'unknown';
  }
}

/**
 * Build properties for page views
 * Uses sanitizeUrl to strip query params and hash from page_url/referrer.
 */
export function buildPageViewProperties(
  pageName: string,
  userId?: string,
  additionalProps?: Record<string, any>
): Record<string, any> {
  let pageUrl: string | undefined;
  let referrer: string | undefined;
  if (typeof window !== 'undefined') {
    pageUrl = sanitizeUrl(window.location.href).substring(0, 200);
  }
  if (typeof document !== 'undefined' && document.referrer) {
    referrer = sanitizeUrl(document.referrer).substring(0, 200);
  }
  return {
    ...buildBaseProperties(userId),
    page_name: pageName,
    page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    page_url: pageUrl,
    referrer,
    ...additionalProps,
  };
}

/**
 * Build properties for feature usage
 */
export function buildFeatureProperties(
  featureName: string,
  action: string,
  userId?: string,
  additionalProps?: Record<string, any>
): Record<string, any> {
  return {
    ...buildBaseProperties(userId),
    feature_name: featureName,
    action,
    ...additionalProps,
  };
}

/**
 * Build properties for errors
 */
export function buildErrorProperties(
  errorType: string,
  errorMessage: string,
  userId?: string,
  additionalProps?: Record<string, any>
): Record<string, any> {
  return {
    ...buildBaseProperties(userId),
    error_type: errorType,
    error_message: errorMessage.substring(0, 200), // Limit message length
    ...additionalProps,
  };
}

/**
 * Build properties for performance metrics
 */
export function buildPerformanceProperties(
  metricName: string,
  value: number,
  unit: string,
  userId?: string,
  additionalProps?: Record<string, any>
): Record<string, any> {
  return {
    ...buildBaseProperties(userId),
    metric_name: metricName,
    metric_value: value,
    metric_unit: unit,
    ...additionalProps,
  };
}
